import React, { useCallback, useEffect, useRef } from 'react';
import { isEmpty, isEqual } from 'lodash';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchDatatableData } from '@actions/data-table-actions';
import Filter from '@components/filters';
import { getDataTypeFilterParams } from '@components/filters/selectors';
import { getDataTableActions } from '@constants/config';
import {
  getDataTableColumns,
  getFilters,
  getLoader,
  optimizeRowsForDataTable
} from '@selectors/data-table-selector';
import Table from '@shared/table';
import ActionsMenu from '@utils/data-table/column-renderers/actions-menu';
import { getEmptyLabel, onRowClick } from '@utils/data-table-utils';
import Footer from './footer';
import Header from './header';
import './data-table.scss';

const getTypes = ({ baseType, dataType }) => {
  if (baseType) {
    return {
      dataType: baseType === 'overlaps' ? 'overlap' : baseType,
      subType: dataType
    };
  }
  return { dataType };
};

const useDataTable = props => {
  const { dataType, subType } = props;
  const columns = useSelector(state => getDataTableColumns(state, props));
  // Read from the correct filter state (state.filters) where Filter component writes
  const filterParams = useSelector(state => getDataTypeFilterParams(state, dataType, subType));
  // Read pagination params from state.dataTables.filters (where Footer/Paginator writes)
  const paginationParams = useSelector(state => getFilters(state, props));
  const loading = useSelector(state => getLoader(state, props));
  const rows = useSelector(state => optimizeRowsForDataTable(dataType, subType)(state));

  return { columns, filterParams, loading, paginationParams, rows };
};

const DataTable = () => {
  const dispatch = useDispatch();
  const params = useParams();
  const { dataType, subType } = getTypes(params);
  const { columns, filterParams, loading, paginationParams, rows } = useDataTable({ dataType, subType });

  // Merge filter params (from Filter component) with pagination params (from Paginator)
  const allParams = { ...filterParams, ...paginationParams };

  // Track previous params to detect actual changes (memoization for deep equality)
  const prevParams = useRef(allParams);
  const paramsChanged = !isEqual(prevParams.current, allParams);
  if (paramsChanged) {
    prevParams.current = allParams;
  }

  useEffect(() => {
    // Fetch when dataType/subType changes or when any params actually change
    dispatch(fetchDatatableData(dataType, subType, allParams));
  }, [dataType, subType, paramsChanged, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Get actions config for this data type (if available)
  const actionsConfig = getDataTableActions('list')?.[dataType] || getDataTableActions('admin')?.[dataType];

  // Create actions renderer that uses the existing ActionsMenu component
  const actionsRenderer = useCallback(row => (
    <ActionsMenu
      actions={actionsConfig}
      dataId={row.id}
      dataType={dataType}
      row={row}
    />
  ), [actionsConfig, dataType]);

  if (!isEmpty(columns) && rows) {
    const tableProps = {
      actionsRenderer: actionsConfig ? actionsRenderer : null,
      columns,
      dataType,
      empty: { message: getEmptyLabel(dataType) },
      header: <Header dataType={dataType} subType={subType} />,
      loading,
      // eslint-disable-next-line no-unused-vars
      onRowClick: (event, row, index) => onRowClick(event, row, dataType, subType, dispatch),
      rows,
      search: <Filter dataType={dataType} subType={subType} layout="horizontal" />,
      stickyFirstColumn: true,
      subType
    };

    return (
      <div styleName="data-table-container">
        <Table {...tableProps} />
        <Footer dataType={dataType} subType={subType} />
      </div>
    );
  }
  return null;
};

export default DataTable;
